import * as forge from 'node-forge';
import ObexRequestsHandlerP80 from '../handlers/ObexRequestsHandlerP80';
import { AeatCredentialsSummary, Society } from '../types/Society.types';
import { getAccessToken, unwrapRecord, unwrapError } from './apiUtils';

export type AeatEnvironment = 'test' | 'prod';

export interface AeatCertificateInfo {
  subject: string;
  serial: string;
  fingerprint: string;
  validFrom: Date;
  validTo: Date;
}

// Los .avap interpolan los valores en la SQL sin escapar, así que todo texto
// libre viaja en base64 (UTF-8) y la SQL lo decodifica en el servidor.
const toBase64 = (value: string): string => btoa(unescape(encodeURIComponent(value)));

/** Datos del certificado PEM. Lanza si no es un certificado legible. */
export const parseAeatCertificate = (certificatePem: string): AeatCertificateInfo => {
  let cert: forge.pki.Certificate;
  try {
    cert = forge.pki.certificateFromPem(certificatePem);
  } catch (e) {
    const found = /-----BEGIN ([A-Z0-9 ]+)-----/.exec(certificatePem);
    if (found) throw new Error(`El fichero del certificado contiene "${found[1]}", que no es un certificado. ¿Has intercambiado el certificado y la clave?`);
    throw new Error(`El fichero del certificado no es un PEM válido (${e instanceof Error ? e.message : 'error desconocido'}).`);
  }
  const der = forge.asn1.toDer(forge.pki.certificateToAsn1(cert)).getBytes();
  const cn = cert.subject.getField('CN');
  return {
    subject:     cn ? String(cn.value) : cert.subject.attributes.map(a => `${a.shortName}=${a.value}`).join(','),
    serial:      cert.serialNumber,
    fingerprint: forge.md.sha256.create().update(der).digest().toHex(),
    validFrom:   cert.validity.notBefore,
    validTo:     cert.validity.notAfter
  };
};

/**
 * Comprueba que la clave privada es la del certificado (misma clave pública
 * RSA). Si la clave no es RSA o está cifrada de un modo que no se puede abrir
 * aquí, no bloquea: devuelve false para que la pantalla lo avise.
 */
export const keyMatchesCertificate = (certificatePem: string, privateKeyPem: string, passphrase: string): boolean => {
  try {
    const cert = forge.pki.certificateFromPem(certificatePem);
    const publicKey = cert.publicKey as forge.pki.rsa.PublicKey;
    let privateKey: forge.pki.rsa.PrivateKey | null = null;
    if (privateKeyPem.indexOf('ENCRYPTED PRIVATE KEY') >= 0 || privateKeyPem.indexOf('ENCRYPTED') >= 0) {
      if (!passphrase) throw new Error('La clave privada está cifrada: indica su contraseña.');
      privateKey = forge.pki.decryptRsaPrivateKey(privateKeyPem, passphrase);
      if (!privateKey) throw new Error('La contraseña de la clave privada no es correcta.');
    } else {
      privateKey = forge.pki.privateKeyFromPem(privateKeyPem) as forge.pki.rsa.PrivateKey;
    }
    return publicKey.n.compareTo(privateKey.n) === 0 && publicKey.e.compareTo(privateKey.e) === 0;
  } catch (e) {
    if (e instanceof Error && /contraseña/.test(e.message)) throw e;
    return false;
  }
};

export const mapAeatSummary = (raw: any): AeatCredentialsSummary => ({
  id:          String(raw.id),
  environment: raw.environment === 'prod' ? 'prod' : 'test',
  ilrCode:     raw.ilr_code || '',
  subject:     raw.cert_subject || '',
  validTo:     raw.cert_valid_to ? String(raw.cert_valid_to) : ''
});

/**
 * Resumen por entorno a partir de una fila de companies-list / companies-get,
 * que trae columnas planas aeat_test_* y aeat_prod_* (null si no hay certificado).
 */
export const mapCompanyAeat = (raw: any): Society['aeat'] | undefined => {
  const read = (env: AeatEnvironment): AeatCredentialsSummary | undefined => {
    const subject = raw[`aeat_${env}_subject`];
    const validTo = raw[`aeat_${env}_valid_to`];
    if (!subject && !validTo) return undefined;
    return {
      id:          '',
      environment: env,
      ilrCode:     raw[`aeat_${env}_ilr_code`] || '',
      subject:     subject || '',
      validTo:     validTo ? String(validTo) : ''
    };
  };
  const test = read('test');
  const prod = read('prod');
  return test || prod ? { test, prod } : undefined;
};

export const saveAeatCredentials = async (payload: {
  companyId: string;
  environment: AeatEnvironment;
  ilrCode: string;
  certificatePem: string;
  privateKeyPem: string;
  passphrase: string;
  info: AeatCertificateInfo;
}): Promise<AeatCredentialsSummary> => {
  const token = getAccessToken();
  if (!token) throw new Error('Sesión no válida o expirada.');

  // PRUEBA: el gateway devuelve 500 con POST, así que de momento va por GET. Con GET el
  // certificado y la clave viajan en la URL (quedan en logs): hay que volver a POST
  // cuando el gateway lo admita.
  const res = await ObexRequestsHandlerP80.get('/companies-aeat-credentials-set', {
    access_token:         token,
    company_id:           payload.companyId,
    environment:          payload.environment,
    ilr_code_b64:         toBase64(payload.ilrCode),
    certificate_b64:      toBase64(payload.certificatePem),
    private_key_b64:      toBase64(payload.privateKeyPem),
    passphrase_b64:       payload.passphrase ? toBase64(payload.passphrase) : undefined,
    cert_subject_b64:     toBase64(payload.info.subject),
    cert_serial_b64:      toBase64(payload.info.serial),
    cert_fingerprint_b64: toBase64(payload.info.fingerprint),
    valid_from_b64:       toBase64(payload.info.validFrom.toISOString()),
    valid_to_b64:         toBase64(payload.info.validTo.toISOString())
  });

  const record = unwrapRecord(res);
  if (!record) throw new Error(unwrapError(res, 'El backend no confirmó el guardado del certificado.'));
  return mapAeatSummary(record);
};
